import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isEmpty } from 'lodash';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { getCurrencySymbol } from 'Components/utils/price/price.util';
import type { BadgeProps } from 'Components/frameworks/badge/Badge';
import 'Components/frameworks/badge/Badge';
import type { DownloadLinksListItem } from 'Components/frameworks/download-links-list/DownloadLinksList';
import { DownloadLinksList } from 'Components/frameworks/download-links-list/DownloadLinksList';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Sheet } from 'Components/frameworks/sheet/Sheet';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import { t, getKeyValue } from 'Src/i18n';
import { ProductFeatureDetailBrandInformation } from './product-feature-detail-brand-information/ProductFeatureDetailBrandInformation';
import { ProductFeatureDetailFacilities } from './product-feature-detail-facilities/ProductFeatureDetailFacilities';
import { ProductFeatureDetailInformations } from './product-feature-detail-informations/ProductFeatureDetailInformations';
import type { ProductFeatureDetailMainInformationProps } from './product-feature-detail-main-information/ProductFeatureDetailMainInformation';
import { ProductFeatureDetailMainInformation } from './product-feature-detail-main-information/ProductFeatureDetailMainInformation';
import { ProductFeatureDetailServices } from './product-feature-detail-services/ProductFeatureDetailServices';
import type { ProductFeatureDetailServiceProps } from './product-feature-detail-services/ProductFeatureDetailService';
import './product-feature-detail-services/ProductFeatureDetailService';
import type { ProductFeatureDetailInformationProps } from './product-feature-detail-informations/ProductFeatureDetailInformation';
import './product-feature-detail-informations/ProductFeatureDetailInformation';
import type { ProductFeatureDetailFacilityProps } from './product-feature-detail-facilities/ProductFeatureDetailFacility';
import './product-feature-detail-facilities/ProductFeatureDetailFacility';
export type ProductFeatureDetailProps = {
  brandInformation?:
    | {
        description: string | null | undefined;
        imageSrc: string | null | undefined;
        title: string;
      }
    | null
    | undefined;
  description?: string;
  diamond?: BadgeProps;
  downloadLinksList?: Array<DownloadLinksListItem>;
  equipmentsTitle?: string;
  equipments?:
    | Array<{
        items:
          | Array<{
              iconName?: IconName;
              label: string;
            }>
          | null
          | undefined;
        title: string;
      }>
    | null
    | undefined;
  facilitiesTitle?: string;
  mainInformationTitle?: string;
  mainInformation?: ProductFeatureDetailMainInformationProps;
  generalInformationTitle?: string;
  generalInformation?: Array<ProductFeatureDetailInformationProps>;
  generalInformationExtraCost?: string;
  immersive: any;
  isIncluded?: boolean;
  notIncludedMessage?: string;
  includedMessage?: string;
  includedServices?: Array<ProductFeatureDetailServiceProps>;
  includedTitle?: string;
  insideFacilities?: Array<ProductFeatureDetailFacilityProps>;
  insideFacilitiesTitle?: string;
  onDemandEquipmentsTitle?: string;
  onDemandServices?: Array<ProductFeatureDetailServiceProps>;
  onDemandTitle?: string;
  outsideFacilities?: Array<ProductFeatureDetailFacilityProps>;
  outsideFacilitiesTitle?: string;
  rentalEquipments?: Array<ProductFeatureDetailServiceProps>;
  targetPrice?: {
    currency: string;
    label: string;
    price: number;
  };
  title: string;
  titleColor?: Colors;
};
export function ProductFeatureDetail({
  brandInformation,
  description,
  diamond,
  downloadLinksList = [],
  facilitiesTitle = '',
  mainInformationTitle,
  mainInformation,
  equipments,
  equipmentsTitle,
  generalInformationTitle,
  generalInformation = [],
  generalInformationExtraCost,
  immersive,
  isIncluded,
  includedMessage,
  notIncludedMessage,
  includedServices = [],
  includedTitle,
  insideFacilities = [],
  insideFacilitiesTitle,
  onDemandEquipmentsTitle,
  onDemandServices = [],
  onDemandTitle,
  outsideFacilities = [],
  outsideFacilitiesTitle,
  rentalEquipments = [],
  targetPrice = {},
  title,
  titleColor = COLORS.BLACK,
}: ProductFeatureDetailProps) {
  const { locale } = useAppContext();
  const footer = (
    <ProductFeatureDetailInformations
      className="my-10"
      items={generalInformation}
      notes={generalInformationExtraCost}
      title={generalInformationTitle}
    />
  );
  const message = isIncluded ? includedMessage : notIncludedMessage;
  const instalmentValue = getKeyValue('common.price.instalmentValue');
  return (
    <Sheet diamond={diamond} footer={footer} immersive={immersive}>
      {!!title && (
        <Title
          className={classnames(
            'justify-center text-center',
            message ? 'mb-10' : 'mb-30',
            `text-${titleColor}`,
          )}
          theme="title-brand-n0"
        >
          {title}
        </Title>
      )}
      {!!message && (
        <p
          className={classnames(
            'mb-30 flex items-center justify-center gap-10 text-center font-brand text-18 sm:text-28',
            {
              'text-green': isIncluded,
              'text-orange': !isIncluded,
            },
          )}
        >
          <Icon name={isIncluded ? ICONS.ALL_INCLUSIVE : ICONS.ON_DEMAND} width="1em" />
          {message}
        </p>
      )}
      <Text className="mb-30" text={description} />
      <DownloadLinksList className="mb-30" links={downloadLinksList} theme="lite" />
      <ProductFeatureDetailMainInformation
        className="my-20"
        smallTitle={
          targetPrice.price ? (
            <TemplateString
              className="font-sans text-14"
              template={targetPrice.label}
              values={{
                currency: getCurrencySymbol(locale, targetPrice.currency),
                price: getKeyValue('common.price.displayInstalmentsPrice')
                  ? t('common.price.instalmentPrice', {
                      instalmentValue,
                      dividedPrice: Math.floor(targetPrice.price / instalmentValue),
                    })
                  : Math.floor(targetPrice.price),
              }}
              valuesClassName={{
                currency: 'text-orange text-20 font-bold',
                price: 'text-orange text-20 font-bold',
              }}
            />
          ) : null
        }
        title={mainInformationTitle}
        {...mainInformation}
      />
      <ProductFeatureDetailBrandInformation
        className="mb-40 mt-20 sm:mb-80"
        description={brandInformation?.description}
        imageSrc={brandInformation?.imageSrc}
        title={brandInformation?.title}
      />
      {!!includedServices.length && (
        <ProductFeatureDetailServices
          className="my-20"
          color={COLORS.GREEN}
          equipments={equipments}
          equipmentsTitle={equipmentsTitle}
          items={includedServices}
          title={includedTitle}
          titleIcon={ICONS.ALL_INCLUSIVE}
        />
      )}
      {!!onDemandServices.length && (
        <ProductFeatureDetailServices
          className="my-20"
          color={COLORS.ORANGE}
          items={onDemandServices}
          title={onDemandTitle}
          titleIcon={ICONS.ON_DEMAND}
        />
      )}
      {!!rentalEquipments.length && (
        <ProductFeatureDetailServices
          className="my-20"
          color={COLORS.ORANGE}
          items={rentalEquipments}
          title={onDemandEquipmentsTitle}
          titleIcon={ICONS.ON_DEMAND}
        />
      )}
      {(!isEmpty(insideFacilities) || !isEmpty(outsideFacilities)) && (
        <section>
          <Title
            className="my-20 justify-center border-b-1 border-black text-center"
            hLevel={3}
            theme="title-brand-n1"
          >
            {facilitiesTitle}
          </Title>
          {!!insideFacilities.length && (
            <ProductFeatureDetailFacilities
              className="my-20"
              items={insideFacilities}
              title={insideFacilitiesTitle}
            />
          )}
          {outsideFacilities && (
            <ProductFeatureDetailFacilities
              className="my-20"
              items={outsideFacilities}
              title={outsideFacilitiesTitle}
            />
          )}
        </section>
      )}
    </Sheet>
  );
}
